import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { Logger } from '../../../infrastructure/logging/logger';
import { ColorProp, ColorPropType, getColor } from '../../../layout/Colors';
import { NavService } from '../../../layout/nav/nav.service';
import { DataTableComponent } from '../../../shared/components/data-table/data-table.component';
import { DataTableColumn } from '../../../shared/components/data-table/DataTableColumn.model';
import { DataTableFilter } from '../../../shared/components/data-table/filters/components/DataTableFilter';
import { SearchService } from '../../../shared/search/search.service';
import { FilterParams, FilterRangeType, SortParams } from '../../../shared/search/SearchParams';
import { dateFormat } from '../../../utilities/DateUtility';
import { NotificationService } from '../../../utilities/PortalNotificationUtility';
import { ClaimsService } from '../../claims/services/claims.service';
import { OrganizationService } from '../../organizations/services/organizations.service';
import { UpdatePolicyToolDialogComponent } from '../../policies/components/update-policy-tool-dialog/update-policy-tool-dialog.component';
import { Policy } from '../../policies/models/Policy';
import { ReservationFilterFromDateComponent } from '../components/filters/reservation-filter-from-date/reservation-filter-from-date.component';
import { ReservationFilterToDateComponent } from '../components/filters/reservation-filter-to-date/reservation-filter-to-date.component';
import { ReservationStatusFilterComponent } from '../components/filters/reservation-status-filter/reservation-status-filter.component';
import { UpdateReservationStatusDialogComponent } from '../components/update-reservation-status-dialog/update-reservation-status-dialog.component';
import { Reservation } from '../models/Reservation';
import { ReservationSearchRecord } from '../models/ReservationSearchRecord';
import { ReservationTableRow } from '../models/ReservationTableRow';
import { canFileClaimForReservation, getFormattedReservationStatus, getReservationStatus as getReservationSearchRecordStatus } from '../reservation-detail/ReservationHelper';
import { ReservationFilterStateService } from '../services/reservation-filter-state.service';
import { ReservationMessageService } from '../services/reservation-message.service';
import { ReservationService } from '../services/reservation.service';

@Component({
  selector: 'app-reservations',
  templateUrl: './reservations.component.html',
  styleUrls: ['./reservations.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatCardModule,
    DataTableComponent,
    MatButtonModule,
    MatProgressSpinner
  ]
})
export class ReservationsComponent implements OnInit {

  @ViewChild(DataTableComponent, { static: false }) dataTableComponent: any;

  filterDefinitions: DataTableFilter[] = [
    { id: 'reservation-filter-status', name: 'Reservation Status', shortName: 'Status', component: ReservationStatusFilterComponent },
    { id: 'reservation-filter-arrival-date', name: 'Arrival Date', shortName: 'Arrival Date', component: ReservationFilterFromDateComponent },
    { id: 'reservation-filter-departure-date', name: 'Departure Date', shortName: 'Departure Date', component: ReservationFilterToDateComponent }
  ];

  displayedColumns: DataTableColumn[] = [
    { column: 'reservationId', display: 'Safely Res. ID' },
    { column: 'referenceId', display: 'Reservation ID' },
    { column: 'organizationId', display: 'Organization ID' },
    { column: 'organizationName', display: 'Organization Name' },
    { column: 'propertyName', display: 'Property Name' },
    { column: 'dates', display: 'Reservation Dates', renderHtml: true },
    { column: 'bookingDate', display: 'Booking Date' },
    { column: 'fullName', display: 'Guest' },
    { column: 'pillStatus', display: 'Status' },
    { column: 'action', display: '' }
  ];

  tableToApiColumnMap: { [key: string]: string } = {
    'reservationId': 'reservationId',
    'referenceId': 'referenceId',
    'organizationId': 'organizationId',
    'organizationName': 'organizationName',
    'propertyName': 'propertyName',
    'dates': 'arrivalDate',
    'bookingDate': 'bookingDate',
    'fullName': 'fullName',
    'pillStatus': 'status',
    'action': ''
  }

  apiToTableColumnMap: { [key: string]: string } = {
    'reservationId': 'reservationId',
    'referenceId': 'referenceId',
    'organizationId': 'organizationId',
    'organizationName': 'organizationName',
    'propertyName': 'propertyName',
    'arrivalDate': 'dates',
    'bookingDate': 'bookingDate',
    'fullName': 'fullName',
    'status': 'pillStatus',
    'action': ''
  }

  dataSource = new MatTableDataSource<ReservationTableRow>();
  mappedReservations: ReservationTableRow[] = [];
  createReservationEnabled = false;
  loading = new BehaviorSubject<boolean>(false);
  tableLength = 0;
  pageSize = 10;
  pageNumber!: number;
  keyword = '';

  organizationId!: any;
  selectedOrganization!: any;
  selectedOrganizationName: string = '';
  reservationStatusUpdate: boolean = false;
  organizationPolicies: Policy[] = [];
  insurancePolicyIdArr: any[] = [];
  filteredPolicyIdArray: any[] = [];
  reservationsToUpdate: any[] = [];
  loadingPolicies: boolean = false;

  getFormattedReservationStatus = getFormattedReservationStatus;

  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);
  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);

  constructor(
    private router: Router,
    private reservationService: ReservationService,
    private reservationMessageService: ReservationMessageService,
    private reservationFilterStateService: ReservationFilterStateService,
    private searchService: SearchService,
    private claimsService: ClaimsService,
    private organizationService: OrganizationService,
    private userContextProvider: UserContextProvider,
    private logService: Logger,
    private navService: NavService,
    private notificationService: NotificationService,
    private dialogRef: MatDialog
  ) { }

  ngOnInit(): void {
    this.searchService.resetQuery();
    this.configureCreateReservationButton();
    this.setupFilterEventHandler();
    this.setupFilterState();
    this.setupSortState();

    this.navService.selectedOrg$.subscribe((org: any) => {
      if (org?.organization_id) {
        this.selectedOrganization = org;
        this.organizationId = org.organization_id;
        this.loadSelectedOrganizationData(this.organizationId);
      } else {
        this.selectedOrganization = undefined;
        this.selectedOrganizationName = '';
        this.loadPage();
      }
    });
  }

  private setupFilterState() {
    if (this.reservationService.lastSearchRequest?.filterParams?.find(f => f.fieldName == 'arrivalDate')) {
      this.updateDateFilterAfterFilterApplied(this.reservationFilterStateService.arrivalDate, 'arrivalDate', FilterRangeType.GREATER_THAN);
    }
    if (this.reservationService.lastSearchRequest?.filterParams?.find(f => f.fieldName == 'departureDate')) {
      this.updateDateFilterAfterFilterApplied(this.reservationFilterStateService.departureDate, 'departureDate', FilterRangeType.LESS_THAN);
    }
    if (this.reservationService.lastSearchRequest?.filterParams?.find(f => f.fieldName == 'status')) {
      this.updateAllStatusFilters();
    }
  }

  private setupSortState() {
    const sort = this.reservationService.lastSearchRequest?.sortParams;
    if (sort) {
      this.searchService.addSort(sort);
      const sortField = this.getSortPropertyNameForTable(sort.sortField);
      const sortDirection = sort.sortOrder == 1 ? 'asc' : 'desc';
      this.initialSort.next({ active: sortField, direction: sortDirection });
    } else {
      this.initialSort.next({ active: 'reservationId', direction: 'desc' });
    }
  }

  configureCreateReservationButton() {
    this.createReservationEnabled = environment.portalConfig.createReservationEnabled && !this.userContextProvider.isEuUser();
  }

  private setupFilterEventHandler() {
    this.reservationMessageService.applyFilterEvent.subscribe(_ => {
      this.updateDateFilterAfterFilterApplied(this.reservationFilterStateService.arrivalDate, 'arrivalDate', FilterRangeType.GREATER_THAN);
      this.updateDateFilterAfterFilterApplied(this.reservationFilterStateService.departureDate, 'departureDate', FilterRangeType.LESS_THAN);

      this.updateAllStatusFilters();
      this.loadPage();
    });
  }

  private updateAllStatusFilters() {
    this.updateStatusFilterAfterFilterApplied(this.reservationFilterStateService.upcoming, 'Upcoming');
    this.updateStatusFilterAfterFilterApplied(this.reservationFilterStateService.isHome, 'In Home');
    this.updateStatusFilterAfterFilterApplied(this.reservationFilterStateService.completed, 'Complete');
    this.updateStatusFilterAfterFilterApplied(this.reservationFilterStateService.canceled, 'Canceled');
    this.updateStatusFilterAfterFilterApplied(this.reservationFilterStateService.claimFiled, 'Claim Filed');
  }

  loadSelectedOrganizationData(selectedOrgId: any) {
    this.selectedOrganization = this.navService.selectedOrg.value;
    this.onOrganizationSearchEvent(selectedOrgId);
    this.getOrganizationPolicies(selectedOrgId);
  }

  async loadPage() {
    if (this.reservationFilterStateService.allStatusFiltersDisabled()) {
      this.updateTableDataSource([], 0);
      return;
    }

    try {
      this.loading.next(true);
      this.dataSource.data = [];

      const { reservations, totalRecords } = await this.reservationService.searchReservations(this.searchService.currentQuery);
      const mappedReservations = (await Promise.allSettled(reservations.map(r => this.buildReservationTableRow(r))))
        .filter(r => r.status === 'fulfilled')
        .map(r => r.value);
      this.updateTableDataSource(mappedReservations, totalRecords);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Reservations', 'Error while retrieving Reservations');
    } finally {
      this.loading.next(false);
    }
  }

  async buildReservationTableRow(reservation: ReservationSearchRecord): Promise<ReservationTableRow> {
    const reservationDates = this.buildReservationTableRowDates(reservation);

    let canFileClaim: boolean = false;
    canFileClaim = this.canFileClaim(reservation);

    let latestClaimId;
    if (reservation.claimCount > 0) {
      latestClaimId = await this.getLatestClaimIdForReservation(reservation.reservationId);
    }

    return {
      id: reservation.reservationId,
      referenceId: reservation.referenceId ?? '',
      reservationId: reservation.reservationIdText,
      organizationId: reservation.organizationId,
      organizationName: reservation.organizationName,
      propertyName: reservation.propertyName,
      pillStatus: String(getReservationSearchRecordStatus(reservation)),
      dates: `${reservationDates.arrivalDate} - ${reservationDates.departureDate}`,
      bookingDate: reservationDates.bookingDate,
      fullName: reservation.fullName,
      action: canFileClaim,
      departureDate: reservation.departureDate,
      latestClaimId: latestClaimId
    }
  }

  private buildReservationTableRowDates(reservation: ReservationSearchRecord): { arrivalDate: string, departureDate: string, bookingDate: string } {
    let arrivalDate = '';
    let departureDate = '';
    let bookingDate = '';

    try {
      if (reservation.arrivalDate) {
        arrivalDate = dateFormat(reservation.arrivalDate, this.userContextProvider.region);
      }

      if (reservation.departureDate) {
        departureDate = dateFormat(reservation.departureDate, this.userContextProvider.region);
      }

      if (reservation.bookingDate) {
        bookingDate = dateFormat(reservation.bookingDate, this.userContextProvider.region);
      }
    } catch (e) {
      this.logService.handleError(`Unable to convert Booking Date: ${reservation.bookingDate}, Arrival Date: ${reservation.arrivalDate} or Departure Date: ${reservation.departureDate} to a date`, `Unable to convert Booking, Arrival or Departure Date`, `Unable to convert Booking Date: ${reservation.bookingDate}, Arrival Date: ${reservation.arrivalDate} or Departure Date: ${reservation.departureDate} to a date`);
    }

    return {
      arrivalDate,
      departureDate,
      bookingDate
    }
  }

  updateTableDataSource(reservations: ReservationTableRow[], totalRecords: number) {
    if (this.selectedOrganization) {
      this.selectedOrganizationName = this.selectedOrganization.organization_name;
    } else {
      this.selectedOrganizationName = '';
    }
    this.dataSource = new MatTableDataSource(reservations);
    this.tableLength = totalRecords;
  }

  onCreateReservationClick() {
    this.router.navigate(['/reservations/form']);
  }

  onPageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.searchService.setPageSize(event.pageSize);
    this.searchService.setPage(event.pageIndex + 1);
    this.loadPage();
  }

  onSearchEvent(keyword: string): void {
    this.addKeywordToQuery(keyword);
    this.loadPage();
  }

  private addKeywordToQuery(keyword: string) {
    this.keyword = keyword;
    this.searchService.addTextQuery(keyword);
    this.searchService.setPage(1);
  }

  onOrganizationSearchEvent(organizationId: any) {
    this.searchService.setOrganizationId(organizationId);
    this.searchService.setPage(1);
    this.loadPage();
  }

  onSortEvent(sortParams: SortParams) {
    this.addSortToQuery(sortParams);
    this.loadPage();
  }

  private addSortToQuery(sortParams: SortParams) {
    sortParams.sortField = this.tableToApiColumnMap[sortParams.sortField];
    this.searchService.addSort(sortParams);
  }

  private getSortPropertyNameForTable(field: string) {
    return this.apiToTableColumnMap[field];
  }

  onReservationActionClick(reservation: ReservationTableRow) {
    this.handleNewClaimButtonClicked(reservation);
  }

  private handleNewClaimButtonClicked(reservation: ReservationTableRow) {
    if (reservation.latestClaimId) {
      this.router.navigate([`/claims/detail/${reservation.latestClaimId}`]);
    } else {
      const reservationId = reservation.id;
      this.router.navigate(['/claims/form'], { queryParams: { reservationId: reservationId } });
    }
  }

  getReservationStatusColor(status: string): string {
    let reservationStatusColorProp: ColorPropType = ColorProp.ReservationStatusDefault;

    switch (status.toUpperCase()) {
      case 'UPCOMING':
        reservationStatusColorProp = ColorProp.ReservationStatusUpcoming;
        break;
      case 'IN HOME':
        reservationStatusColorProp = ColorProp.ReservationStatusInHome;
        break;
      case 'COMPLETE':
        reservationStatusColorProp = ColorProp.ReservationStatusComplete;
        break;
      case 'CLAIM FILED':
        reservationStatusColorProp = ColorProp.ReservationStatusClaimFiled;
        break;
      case 'CANCELED':
        reservationStatusColorProp = ColorProp.ReservationStatusCanceled;
        break;
    }

    return getColor(reservationStatusColorProp);
  }

  updateStatusFilterAfterFilterApplied(statusEnabled: boolean, statusName: string) {
    if (statusEnabled) {
      this.updateStatusFilter(statusName);
    } else {
      this.removeStatusFilterValue(statusName);
    }
  }

  removeStatusFilterValue(statusValue: string) {
    this.searchService.removeValueFromFilter('status', statusValue);
  }

  updateStatusFilter(statusValue: string) {
    this.searchService.updateFilter({
      fieldName: 'status',
      value: [statusValue],
      range: false
    });
  }

  updateDateFilterAfterFilterApplied(dateValue: string, filterName: string, rangeType: number) {
    if (dateValue) {
      this.addDateFilter(dateValue, filterName, rangeType);
    } else {
      this.removeDateFilter(filterName);
    }
  }

  addDateFilter(dateValue: string, filterName: string, rangeType: number) {
    if (!dateValue || !filterName) {
      return;
    }

    const formattedDate = formatDate(dateValue, 'yyyy-MM-dd', 'en-US', 'UTC');
    const filter: FilterParams = {
      fieldName: filterName,
      value: [formattedDate],
      range: true,
      rangeType: rangeType
    };
    this.searchService.replaceFilter(filter);
  }

  removeDateFilter(filterName: string) {
    this.searchService.removeFilter(filterName);
  }

  private canFileClaim(reservation: ReservationSearchRecord): boolean {
    const status = getReservationSearchRecordStatus(reservation);
    if (!status) {
      this.logService.handleError(`Unable to determine reservation status for reservation ${reservation.reservationId}`, 'Unable to determine reservation status', `Unable to determine reservation status for reservation ${reservation.reservationId}`);
      return false;
    }

    return canFileClaimForReservation(reservation.departureDate, status, this.userContextProvider.isNaUser());
  }

  getNewClaimButtonBackgroundColor(row: ReservationTableRow): string {
    if (!row.departureDate) {
      return '';
    }

    return getColor(ColorProp.NewClaimAfter60Days);
  }

  private async getLatestClaimIdForReservation(reservationId: number): Promise<string> {
    try {
      const response = await this.claimsService.getClaims({
        filter: { draft: true, submitted: true, completed: true },
        page: 1,
        pageSize: 1,
        reservationId: reservationId
      });
      if (response?.totalRecords > 0 && response?.claims?.[0]?.id) {
        return response.claims[0].id;
      } else {
        return '';
      }
    } catch (e) {
      this.logService.handleError(e, `Error while calculating new Claim button text for reservation`, `Error while calculating new Claim button text for reservation: ${reservationId}`);
    }

    return '';
  }

  getActionButtonText(reservation: ReservationTableRow): string {
    return reservation.latestClaimId ? `Claim ${reservation.latestClaimId}` : 'New Claim';
  }

  getActionButtonIcon(reservation: ReservationTableRow): string {
    return reservation.latestClaimId ? 'edit' : 'add';
  }


  // ADMIN
  async getOrganizationPolicies(organizationId: number) {
    this.organizationPolicies = await this.organizationService.getPoliciesByOrganizationId(organizationId);
  }

  async getSelectedReservationDetails(reservations: any[]) {
    this.reservationsToUpdate = [];

    try {
      this.loadingPolicies = true;
      this.organizationPolicies = [];
      this.organizationPolicies = await this.organizationService.getPoliciesByOrganizationId(reservations[0].organizationId);

      this.reservationsToUpdate = await Promise.all(
        reservations.map(reservation => this.reservationService.getReservationById(reservation.id))
      );

      if (this.organizationPolicies && this.reservationStatusUpdate) {
        this.openUpdateReservationStatusDialog();
      } else if (this.organizationPolicies && !this.reservationStatusUpdate) {
        this.openAssignReservationPolicyDialog();
      }
    } catch(e) {
      this.logService.handleError(e, 'No Organization policies found', 'No Organization policies found');
    } finally {
      this.loadingPolicies = false;
    }
  }

  onReservationUpdateStatusEvent(row: any) {
    this.reservationStatusUpdate = true;
    let reservationsSelected = row.selected;
    this.getSelectedReservationDetails(reservationsSelected);
  }

  openUpdateReservationStatusDialog() {
    const statusDialogRef = this.dialogRef.open(UpdateReservationStatusDialogComponent, {
      width: '60%',
      maxHeight: '400px',
      disableClose: true,
      data: this.reservationsToUpdate
    });
    statusDialogRef.afterClosed().subscribe(statusPayload => {
      if (statusPayload.statusId > 0) {
        try {
          this.reservationsToUpdate.forEach(reservation => {
            const updatePayload = this.buildUpdatePayload(statusPayload, reservation);
            const infoPayload = this.buildInfoPayload(reservation);

            this.reservationService.updateReservationStatus(reservation, updatePayload).then(_ => {
              this.reservationService.updateReservationInfo(reservation, infoPayload).then(async _ => {
                await this.loadPage();
              });
            });
          });
        } catch (e) {
          this.logService.handleError(e, 'An error occurred while updating Reservation status', 'Error while updating Reservation status');
        }
      }
    });
  }

  buildUpdatePayload(statusPayload: any, reservation: any) {
    const updatePayload = {
      id: reservation.reservation_id.toString(),
      status_date: statusPayload.statusDate,
      status_id: statusPayload.statusId
    }
    return updatePayload;
  }

  buildInfoPayload(reservation: any) {
    const infoPayload = {
      exclude_from_invoice: 1,
      id: reservation.reservation_id.toString(),
      verification_paid: reservation.verification_paid
    }
    return infoPayload;
  }

  onReservationUpdatePolicyEvent(row: any) {
    this.reservationStatusUpdate = false;
    let reservationsToUpdate = row.selected;
    this.getSelectedReservationDetails(reservationsToUpdate);
  }

  openAssignReservationPolicyDialog() {
    const assignDialogRef = this.dialogRef.open(UpdatePolicyToolDialogComponent, {
      width: '90%',
      maxHeight: '750px',
      disableClose: true,
      data: { 
        policies: this.organizationPolicies,
        component: 'reservations'
      }
    });
    assignDialogRef.afterClosed().subscribe(selectedPolicyId => {
      if (selectedPolicyId > 0) {
        let newPolicyIdArr: any[] = [];
        newPolicyIdArr.push(selectedPolicyId);

        try {
          let ctr = 0;
          this.reservationsToUpdate.forEach(reservation => {
            ctr++;
            reservation.selected_policies = newPolicyIdArr;
            const reservationUpdatePayload = this.buildUpdatePolicyPayload(reservation);

            this.reservationService.updateReservation(reservation.reservation_id, reservationUpdatePayload).then(_ => {
              this.reservationService.getReservationById(reservation.reservation_id);

              if (ctr === this.reservationsToUpdate.length) {
                this.notificationService.showNotification({ message: `Reservation policies updated successfully!` })
              }
            });
          });
        } catch (e) {
          this.logService.handleError(e, 'An error occurred while assigning Reservation policy', 'Error while assigning Reservation policy');
        }
      }
    });
  }

  buildUpdatePolicyPayload(reservation: Reservation) {
    let updatePolicyPayload = {
      reservation_id: reservation.reservation_id,
      reservation_reference_id: reservation.reservation_reference_id,
      arrival_date: reservation.arrival_date,
      departure_date: reservation.departure_date,
      booking_date: reservation.booking_date,
      number_of_guests: reservation.number_of_guests,
      reservation_cost: reservation.reservation_cost,
      reservation_currency_id: reservation.reservation_currency_id,
      property_id: reservation.property.property_id,
      guests: reservation.guests,
      organization_id: reservation.organization_id,
      selected_policies: reservation.selected_policies
    }
    return updatePolicyPayload;
  }

  onImportReservationDataClick() {
    this.router.navigate(['/reservations/import']);
  }
}